import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  Button,
  Divider,
  List,
  ListItem,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert
} from '@mui/material';
import { useNavigate, Link } from 'react-router-dom';
import EmailIcon from '@mui/icons-material/Email';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SettingsIcon from '@mui/icons-material/Settings';
// import { adminServices } from '../services/api';

// Mock data for emails
const mockEmails = [
  {
    id: 1,
    sender: 'Wanjiku Kamau',
    email: 'wanjiku.kamau@example.com',
    subject: 'Property Inquiry - Downtown Loft',
    preview: 'Hello, I am interested in the downtown loft property and would like to schedule a viewing...',
    date: 'Mar 28, 2025',
    read: false
  },
  {
    id: 2,
    sender: 'Otieno Odhiambo',
    email: 'otieno.o@example.com',
    subject: 'Maintenance Request Follow-up',
    preview: 'I wanted to follow up on the maintenance request I submitted last week regarding the...',
    date: 'Mar 27, 2025',
    read: true
  },
  {
    id: 3,
    sender: 'Njeri Mwangi',
    email: 'njeri.m@example.com',
    subject: 'Rental Application Status',
    preview: 'I submitted my rental application for the Oakwood property last Tuesday and was wondering...',
    date: 'Mar 26, 2025',
    read: true
  }
];

// Mock data for contact form submissions
const mockContactSubmissions = [
  {
    id: 1,
    name: 'Kimani Waweru',
    email: 'kimani.waweru@example.com',
    phone: '(+254) 712-345-678',
    message: 'I am looking for a property management company to handle my 3 rental properties in the downtown area. Would like to discuss your services.',
    date: 'Mar 28, 2025',
    viewed: false
  },
  {
    id: 2,
    name: 'Akinyi Onyango',
    email: 'akinyi.o@example.com',
    phone: '(+254) 723-987-654',
    message: 'Interested in your luxury renovation services for my penthouse. Please contact me to discuss details.',
    date: 'Mar 27, 2025',
    viewed: true
  },
  {
    id: 3,
    name: 'Mutua Kioko',
    email: 'mutua.k@example.com',
    phone: '(+254) 735-456-789',
    message: 'Looking for information about your MRI tax support services. What documentation do I need to provide?',
    date: 'Mar 26, 2025',
    viewed: true
  }
];

const Dashboard = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [theme, setTheme] = useState('dark');
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  
  useEffect(() => {
    // Check if user is authenticated as admin
    const adminAuth = localStorage.getItem('isAdminAuthenticated');
    if (adminAuth !== 'true') {
      // Redirect to admin login if not authenticated
      navigate('/admin-login');
    } else {
      setIsAuthenticated(true);
      
      // Fetch admin settings
      fetchAdminSettings();
    }
  }, [navigate]);
  
  const fetchAdminSettings = async () => {
    try {
      // In a real implementation, this would call your API endpoint
      // const response = await adminServices.getSettings();
      // setTheme(response.data.settings.theme || 'dark');
      
      // For now, we're using localStorage as a mock
      const savedTheme = localStorage.getItem('adminTheme') || 'dark';
      setTheme(savedTheme);
    } catch (error) {
      console.error('Error fetching admin settings:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('isAdminAuthenticated');
    navigate('/admin-login');
  };

  const handleThemeChange = async (event) => {
    const newTheme = event.target.value;
    setTheme(newTheme);
    
    try {
      // In a real implementation, this would call your API endpoint
      // await adminServices.updateSettings({ theme: newTheme });
      
      // For now, we're using localStorage as a mock
      localStorage.setItem('adminTheme', newTheme);
      
      showSnackbar('Theme updated successfully', 'success');
    } catch (error) {
      console.error('Error updating theme:', error);
      showSnackbar('Failed to update theme', 'error');
    }
  };
  
  const handlePasswordDialogOpen = () => {
    setPasswordDialogOpen(true);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
  };
  
  const handlePasswordDialogClose = () => {
    setPasswordDialogOpen(false);
  };
  
  const handlePasswordChange = async () => {
    // Basic validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }
    
    try {
      // In a real implementation, this would call your API endpoint
      // await adminServices.updatePassword(currentPassword, newPassword);
      
      // For demo, we'll just simulate success
      setPasswordDialogOpen(false);
      showSnackbar('Password updated successfully', 'success');
    } catch (error) {
      console.error('Error updating password:', error);
      setPasswordError('Current password is incorrect');
    }
  };
  
  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };
  
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  if (!isAuthenticated) {
    return null; // Don't render anything while checking authentication
  }

  return (
    <Box sx={{ backgroundColor: '#fbfbfb', minHeight: '100vh', pt: 4, pb: 8 }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1" sx={{ color: '#0a233b', fontWeight: 'bold' }}>
            Admin Dashboard
          </Typography>
          <Box>
            <Button 
              variant="outlined" 
              component={Link}
              to="/admin-emails"
              sx={{ 
                mr: 2,
                borderColor: '#a6d1ea', 
                color: '#0a233b',
                '&:hover': { 
                  borderColor: '#0a233b', 
                  backgroundColor: 'rgba(166, 209, 234, 0.1)' 
                } 
              }}
              startIcon={<EmailIcon />}
            >
              Email Center
            </Button>
            <Button 
              variant="contained" 
              onClick={handleLogout}
              sx={{ 
                backgroundColor: '#f66b10', 
                '&:hover': { backgroundColor: '#e05a00' } 
              }}
            >
              Logout
            </Button>
          </Box>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#0a233b' }}>
              Quick Stats
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ backgroundColor: '#e8f4fd', height: '100%' }}>
                  <CardContent>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>42</Typography>
                    <Typography variant="body2" color="text.secondary">Properties Managed</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ backgroundColor: '#e8f4fd', height: '100%' }}>
                  <CardContent>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>128</Typography>
                    <Typography variant="body2" color="text.secondary">Active Tenants</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ backgroundColor: '#e8f4fd', height: '100%' }}>
                  <CardContent>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>$86,420</Typography>
                    <Typography variant="body2" color="text.secondary">Monthly Revenue</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ backgroundColor: '#e8f4fd', height: '100%' }}>
                  <CardContent>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>7</Typography>
                    <Typography variant="body2" color="text.secondary">Active Projects</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Box>

        <Grid container spacing={4}>
          {/* Email Preview Section */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ color: '#0a233b' }}>
                  <EmailIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Recent Emails
                </Typography>
                <Button 
                  component={Link} 
                  to="/admin-emails"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ 
                    color: '#f66b10',
                    '&:hover': { backgroundColor: 'rgba(246, 107, 16, 0.1)' } 
                  }}
                >
                  See All
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              <List sx={{ p: 0 }}>
                {mockEmails.map((email) => (
                  <ListItem 
                    key={email.id}
                    sx={{ 
                      mb: 1, 
                      p: 2,
                      backgroundColor: email.read ? '#f5f5f5' : '#fff4e5', 
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: email.read ? '#e0e0e0' : '#f66b10',
                      position: 'relative'
                    }}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: email.read ? 'normal' : 'bold' }}>
                          {email.sender}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {email.date}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: email.read ? 'normal' : 'bold', mb: 1 }}>
                        {email.subject}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {email.preview}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                        <IconButton 
                          component={Link}
                          to={`/admin-emails/view/${email.id}`}
                          size="small"
                          sx={{ color: '#0a233b' }}
                        >
                          <MoreHorizIcon />
                        </IconButton>
                      </Box>
                      {!email.read && (
                        <Chip 
                          label="New" 
                          size="small" 
                          sx={{ 
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            backgroundColor: '#f66b10',
                            color: 'white',
                            fontSize: '0.7rem',
                            height: 20
                          }} 
                        />
                      )}
                    </Box>
                  </ListItem>
                ))}
              </List>
              
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Button 
                  variant="contained"
                  component={Link}
                  to="/admin-emails/compose"
                  sx={{ 
                    backgroundColor: '#0a233b', 
                    '&:hover': { backgroundColor: '#0a233b' } 
                  }}
                  startIcon={<EmailIcon />}
                >
                  Compose New Email
                </Button>
              </Box>
            </Paper>
          </Grid>
          
          {/* Contact Form Submissions Section */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ color: '#0a233b' }}>
                  <ContactMailIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Contact Form Submissions
                </Typography>
                <Button 
                  component={Link} 
                  to="/admin-contacts"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ 
                    color: '#f66b10',
                    '&:hover': { backgroundColor: 'rgba(246, 107, 16, 0.1)' } 
                  }}
                >
                  See All
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              <List sx={{ p: 0 }}>
                {mockContactSubmissions.map((submission) => (
                  <ListItem 
                    key={submission.id}
                    sx={{ 
                      mb: 1, 
                      p: 2,
                      backgroundColor: submission.viewed ? '#f5f5f5' : '#fff4e5', 
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: submission.viewed ? '#e0e0e0' : '#f66b10',
                      position: 'relative'
                    }}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: submission.viewed ? 'normal' : 'bold' }}>
                          {submission.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {submission.date}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
                          {submission.email}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {submission.phone}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {submission.message}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                        <IconButton 
                          component={Link}
                          to={`/admin-contacts/view/${submission.id}`}
                          size="small"
                          sx={{ color: '#0a233b' }}
                        >
                          <MoreHorizIcon />
                        </IconButton>
                      </Box>
                      {!submission.viewed && (
                        <Chip 
                          label="New" 
                          size="small" 
                          sx={{ 
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            backgroundColor: '#f66b10',
                            color: 'white',
                            fontSize: '0.7rem',
                            height: 20
                          }} 
                        />
                      )}
                    </Box>
                  </ListItem>
                ))}
              </List>
              
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Button 
                  variant="outlined"
                  component={Link}
                  to="/admin-contacts"
                  sx={{ 
                    borderColor: '#a6d1ea', 
                    color: '#0a233b',
                    '&:hover': { 
                      borderColor: '#0a233b', 
                      backgroundColor: 'rgba(166, 209, 234, 0.1)' 
                    } 
                  }}
                  startIcon={<ContactMailIcon />}
                >
                  View All Submissions
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Admin Settings Section */}
        <Box sx={{ mt: 4, mb: 4 }}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ color: '#0a233b' }}>
                <SettingsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Admin Settings
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={3}>
              {/* Theme Settings */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium' }}>
                  Dashboard Theme
                </Typography>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="theme-select-label">Theme</InputLabel>
                  <Select
                    labelId="theme-select-label"
                    id="theme-select"
                    value={theme}
                    onChange={handleThemeChange}
                    label="Theme"
                  >
                    <MenuItem value="light">Light</MenuItem>
                    <MenuItem value="dark">Dark</MenuItem>
                    <MenuItem value="blue">Blue</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                  Choose the theme for your admin dashboard experience.
                </Typography>
              </Grid>
              
              {/* Password Settings */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium' }}>
                  Account Security
                </Typography>
                <Button 
                  variant="outlined" 
                  onClick={handlePasswordDialogOpen}
                  sx={{ 
                    borderColor: '#a6d1ea', 
                    color: '#0a233b',
                    '&:hover': { 
                      borderColor: '#0a233b', 
                      backgroundColor: 'rgba(166, 209, 234, 0.1)' 
                    } 
                  }}
                >
                  Change Password
                </Button>
                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                  Your email address is <strong>info@lantenapm.com</strong>. This email cannot be changed.
                </Typography>
              </Grid>
              
              {/* Other Settings can be added here */}
            </Grid>
          </Paper>
        </Box>
      </Container>
      
      {/* Password Change Dialog */}
      <Dialog open={passwordDialogOpen} onClose={handlePasswordDialogClose}>
        <DialogTitle>Change Admin Password</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter your current password and a new password to update your admin credentials.
          </DialogContentText>
          
          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordError}
            </Alert>
          )}
          
          <TextField
            autoFocus
            margin="dense"
            id="current-password"
            label="Current Password"
            type="password"
            fullWidth
            variant="outlined"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            id="new-password"
            label="New Password"
            type="password"
            fullWidth
            variant="outlined"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            id="confirm-password"
            label="Confirm New Password"
            type="password"
            fullWidth
            variant="outlined"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePasswordDialogClose} sx={{ color: '#0a233b' }}>
            Cancel
          </Button>
          <Button 
            onClick={handlePasswordChange} 
            variant="contained"
            sx={{ 
              backgroundColor: '#f66b10', 
              '&:hover': { backgroundColor: '#e05a00' } 
            }}
          >
            Update Password
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity} 
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Dashboard; 